<template>
  <div class="wf-files-container">
    <ul class="wf-files-wrapper" :class="{ 'has-controls': showControls }">
      <li v-if="wfFiles.length > 0" class="wf-file wf-file-header">
        <span class="file-status" />
        <h3 class="title mb-1 is-7 file-info">Files</h3>
        <h3 class="title mb-1 is-7 file-type">File Types</h3>
        <h3 v-if="showControls" class="title mb-1 is-7 file-controls" />
      </li>
      <li
        v-for="(file, index) of wfFiles"
        :key="`f${index}-${file.id.value || file.id}`"
        class="wf-file"
      >
        <WorkflowFileRow :workflow-file="file" :is-edit-mode="isEditMode">
          <template
            v-if="isEditMode && fileOptions.length > 0"
            #file-name="{ isProcessing }"
          >
            <SelectContainer
              :key="`f${index}-${file.file_path}`"
              class="wf-file-select no-deselect"
              placeholder="Select workflow file"
              track-by="file_path"
              label="file_path"
              :disabled="isProcessing"
              :options="nonSelectedFileOptions"
              :value="file || null"
              :validation-rules="[
                {
                  name: 'required',
                  error_message: 'Select a file',
                },
              ]"
              @input="
                (fileOpt) =>
                  fileOpt &&
                  $emit('change-file', {
                    file: {
                      ...file,
                      file_path: fileOpt.file_path,
                      data_type: fileOpt.data_type,
                      id: file.id,
                    },
                  })
              "
            >
              <template #option="{ option }">
                {{ option.file_path }}
              </template>
            </SelectContainer>
          </template>
          <template v-if="isEditMode" #file-type="{ isProcessing }">
            <SelectContainer
              class="wf-file-type-select no-deselect"
              placeholder="Select workflow file type"
              :options="
                typeOptions.filter((o) => o.value !== file.data_type?.value)
              "
              :deselect-label="'✓'"
              track-by="value"
              label="label"
              :loading="isProcessing"
              :disabled="isProcessing || !isEditMode"
              :value="file.data_type"
              @input="
                (optValue) =>
                  $emit('change-file-type', {
                    file: { ...file, data_type: optValue },
                  })
              "
            />
          </template>
          <template #controls="{ isRestorable, isProcessing }">
            <fieldset
              v-if="showControls"
              :disabled="isProcessing"
              style="display: contents"
            >
              <div class="wf-controls">
                <IconButton
                  v-if="canRestoreFiles"
                  icon="fa-solid fa-rotate-left"
                  color="primary"
                  tooltip="Restore last version"
                  :disabled="!isRestorable || isProcessing"
                  @click="$emit('restore-file', { file })"
                />
                <Popover v-if="!isRemoteWorkflow" ref="deletePopover">
                  <IconButton
                    icon="fa-solid fa-trash"
                    color="danger"
                    colorful
                    :disabled="isProcessing"
                  />
                  <template #popper="{ hide }">
                    <div class="is-danger is-flex is-flex-direction-column p-1">
                      <div class="pb-4">
                        Once deleted, the file cannot be recovered and you will
                        have to upload a new file
                      </div>
                      <button
                        class="button is-small is-danger"
                        :class="{ 'is-loading': isProcessing }"
                        :disabled="isProcessing"
                        @click="
                          () => {
                            hide()
                            $emit('delete-file', { file })
                          }
                        "
                      >
                        <span class="icon">
                          <i class="fa-solid fa-trash" />
                        </span>
                        <span>Delete</span>
                      </button>
                    </div>
                  </template>
                </Popover>
                <IconButton
                  v-else
                  icon="fa-solid fa-trash"
                  color="danger"
                  colorful
                  :disabled="isProcessing"
                  @click="$emit('delete-file', { file })"
                />
              </div>
            </fieldset>
          </template>
        </WorkflowFileRow>
      </li>
    </ul>
  </div>
</template>

<script>
import { computed, watch, ref } from 'vue'
import WorkflowFileRow from './WorkflowFileRow.vue'
import SelectContainer from '@/components/SelectContainer.vue'

export default {
  name: 'WorkflowFiles',
  emits: ['change-file', 'change-file-type', 'restore-file', 'delete-file'],
  components: {
    SelectContainer,
    WorkflowFileRow,
  },
  props: {
    workflowFiles: {
      type: Object,
      default: () => ({}),
    },
    fileOptions: {
      type: Array,
      default: () => [],
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
    canDeleteFiles: {
      type: Boolean,
      default: false,
    },
    canRestoreFiles: {
      type: Boolean,
      default: false,
    },
    typeOptions: {
      type: Array,
      default: () => [],
    },
    isRemoteWorkflow: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const wfFiles = ref([])

    watch(
      () => props.workflowFiles,
      (files) => (wfFiles.value = Object.values(files)),
      { immediate: true, deep: true },
    )

    const workflowFilePaths = computed(() =>
      wfFiles.value.map((f) => f.file_path),
    )
    const nonSelectedFileOptions = computed(() =>
      props.fileOptions.filter(
        (f) => !workflowFilePaths.value.includes(f.file_path),
      ),
    )
    const showControls = computed(
      () => props.isEditMode && props.canDeleteFiles,
    )

    return {
      wfFiles,
      nonSelectedFileOptions,
      showControls,
    }
  },
}
</script>

<style lang="scss" scoped>
.wf-files-container {
  container-name: wf-files;
  container-type: inline-size;
}

.wf-controls {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

// --- Narrow: stacked per-row grid ---
@container wf-files (max-width: #{$cq-sm - 1}) {
  ul.wf-files-wrapper {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  li.wf-file {
    display: grid;
    grid-template-columns: 32px 1fr auto;
    grid-template-rows: auto auto;
    gap: 0.15rem 8px;
    align-items: center;

    &.wf-file-header {
      display: none;
    }
  }

  li.wf-file :deep(.file-status),
  li.wf-file > .file-status {
    grid-column: 1;
    grid-row: 1 / 3;
    align-self: center;
  }

  li.wf-file :deep(.file-info),
  li.wf-file > .file-info {
    grid-column: 2 / -1;
    grid-row: 1;
  }

  li.wf-file :deep(.file-type),
  li.wf-file > .file-type {
    grid-column: 2;
    grid-row: 2;
    min-width: 0;
  }

  li.wf-file :deep(.file-controls),
  li.wf-file > .file-controls {
    grid-column: 3;
    grid-row: 2;
  }
}

// --- Wide: shared-column flat grid ---
@container wf-files (min-width: #{$cq-sm}) {
  ul.wf-files-wrapper {
    display: grid;
    grid-template-columns: 40px 1fr max-content;
    gap: 0.5rem 10px;
    align-items: center;

    &.has-controls {
      grid-template-columns: 40px 1fr max-content max-content;
    }
  }

  li.wf-file {
    display: contents;

    &.is-editing :deep(.file-type) {
      min-width: 150px;
    }
  }
}
</style>
